<i18n locale="en">
{
  "labels": {
    "date": "Date",
    "time": "Time",
    "subject": "Subject",
    "location": "Location"
  }
}
</i18n>

<template>
  <div class="flex-col gap-6">
    <OwnInputContainer
      v-if="editSubject"
      :label="t('labels.subject')"
      :errors="subjectLine.status.errors"
      :max-chars="MAX_SUBJECT_LENGTH"
      :value="subjectLine.value"
    >
      <OwnInput v-model="subjectLine.value" />
    </OwnInputContainer>
    <OwnInputContainer
      v-if="editAudience"
      :label="t('labels.location')"
      :errors="audienceLocationId.status.errors"
      :value="audienceLocationId.value"
    >
      <LocationSelector
        v-model="audienceLocationId.value"
        :force-selection="!!audienceLocationId.value"
      />
    </OwnInputContainer>
    <div v-if="editSchedule" class="flex-col">
      <div class="otc-editor__schedule-row align-start flex-row gap-3">
        <OwnInputContainer
          :label="t('labels.date')"
          :errors="day.status.errors"
        >
          <DatePicker
            v-model="day.value"
            future
            data-testid="schedule-date-input"
          />
        </OwnInputContainer>
        <div class="otc-editor__time-input flex-col gap-1">
          <OwnInputContainer
            :label="t('labels.time')"
            :errors="time.status.errors"
          >
            <TimePicker
              v-model="time.value"
              :same-day="sameDay"
              data-testid="schedule-time-input"
            />
          </OwnInputContainer>
          <OwnType :text="timezone" variant="paragraph-small" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, isBefore, parse, parseISO, startOfDay } from 'date-fns'
import { useI18n } from 'vue-i18n'

import { DatePicker, TimePicker } from '@/components/inputs/date-time'
import LocationSelector from '@/components/selectors/LocationSelector.vue'
import { FormBuilder, Validators } from '@/forms'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnType } from '@/ui/OwnType'
import { getLocalizedTimezone } from '@/utils/getLocalizedTimezone'

const MAX_SUBJECT_LENGTH = 200

export default {
  name: 'OTCEditor',
  components: {
    DatePicker,
    LocationSelector,
    OwnInput,
    OwnInputContainer,
    OwnType,
    TimePicker,
  },
  mixins: [
    FormBuilder({
      audienceLocationId: {
        // Field has no validators — initialize as valid so it never holds
        // back `isFormValid` (its `.value` may stay undefined when the
        // editor is mounted without a `locationId` prop, in which case the
        // value-watcher never fires and validation would never run).
        validateOnInit: true,
        validators: [],
      },
      day: {
        validateOnInit: false,
        validators: [
          Validators.required,
          (value) =>
            value && isBefore(parseISO(value), startOfDay(new Date()))
              ? { errors: ['Date must be in the future'], valid: false }
              : { errors: [], valid: true },
        ],
      },
      subjectLine: {
        validateOnInit: false,
        validators: [
          Validators.required,
          Validators.maxLength(MAX_SUBJECT_LENGTH),
        ],
      },
      time: {
        validateOnInit: false,
        validators: [Validators.required],
      },
    }),
  ],
  props: {
    /**
     * A synced prop for the date of the campaign
     */
    date: { type: Date, default: () => new Date() },

    /**
     * A synced prop indicating form validity
     */
    disabled: { type: Boolean, default: false },

    /** Allows editing the campaign audience location */
    editAudience: { type: Boolean, default: false },

    /** Allows editing the campaign scheduled date */
    editSchedule: { type: Boolean, default: false },

    /** Allows editing the campaign subject  */
    editSubject: { type: Boolean, default: false },

    /**
     * A synced prop for the location ID of the campaign
     */
    locationId: { type: String, default: undefined },

    /**
     * A synced prop for the subject of the campaign
     */
    subject: { type: String, default: undefined },
  },
  emits: [
    'update:date',
    'update:subject',
    'update:locationId',
    'update:disabled',
  ],
  setup() {
    const { t } = useI18n()

    return { t }
  },
  data() {
    return {
      MAX_SUBJECT_LENGTH,
      timezone: getLocalizedTimezone(),
    }
  },
  computed: {
    sameDay() {
      return this.day.value === format(new Date(), 'yyyy-MM-dd')
    },
  },
  watch: {
    audienceLocationId: {
      deep: true,
      handler() {
        this.$emit('update:locationId', this.audienceLocationId.value)
      },
    },
    date: 'initialize',
    day: {
      deep: true,
      handler: 'updateScheduledDate',
    },
    isFormValid(isFormValid) {
      this.$emit('update:disabled', !isFormValid)
    },
    locationId: 'initialize',
    sameDay(sameDay, previousDateWasSameDay) {
      if (previousDateWasSameDay === sameDay) return
      this.time.value = sameDay ? format(new Date(), 'h:mm a') : '9:00 AM' // We need to prevent "Now" from being selected when the date changes
    },
    subject: 'initialize',
    subjectLine: {
      deep: true,
      handler() {
        this.$emit('update:subject', this.subjectLine.value)
      },
    },
    time: {
      deep: true,
      handler() {
        this.updateScheduledDate()
      },
    },
  },
  mounted() {
    this.initialize()
  },
  methods: {
    initialize() {
      const date = this.date ?? new Date()
      this.subjectLine.value = this.subject
      this.audienceLocationId.value = this.$props.locationId
      this.day.value = format(date, 'yyyy-MM-dd', new Date())
      this.time.value = format(date, 'h:mm a', new Date())
    },
    updateScheduledDate() {
      const { day, time } = this

      this.$emit(
        'update:date',
        parse(`${day.value} ${time.value}`, 'yyyy-MM-dd h:mm a', new Date())
      )
    },
  },
}
</script>

<style lang="scss" scoped>
// Reserves a 1px border on DatePicker's OwnInput to match TimePicker's
// OwnCard wrapper height, and normalizes the dropdown's font weight.
// Safe across states: OwnInput signals error/focus via box-shadow, not border.
.otc-editor__schedule-row {
  :deep(.own-input) {
    border: 1px solid transparent;
  }

  :deep(.own-dropdown__input) {
    font-weight: 400;
  }
}
</style>
